import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ZOHO_PAYMENTS } from "../../config/zohoPayments";
import "./PayButton.css";

let widgetScriptPromise = null;
function loadZohoWidgetScript() {
  if (window.ZPayments) return Promise.resolve();
  if (!widgetScriptPromise) {
    widgetScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://static.zohocdn.com/zpay/zpay-js/v1/zpayments.js";
      script.async = true;
      script.onload = resolve;
      script.onerror = () => {
        widgetScriptPromise = null;
        reject(new Error("Could not load the payment widget. Check your connection and try again."));
      };
      document.body.appendChild(script);
    });
  }
  return widgetScriptPromise;
}

// Pays a fixed booking advance for an e-cycle purchase. `note` (e.g. the
// bike model) is passed through to Zoho as metadata purely so the advance
// can be matched back to the right booking — the balance is still settled
// in person, not through this flow.
export default function PayButton({ productKey = "ecycle-booking", note = "", label = "Pay Booking Advance" }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handlePay(e) {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setError("Please enter your name and phone number.");
      return;
    }
    if (!ZOHO_PAYMENTS.apiKey) {
      setError("Online payment isn't configured yet. Please contact us to book instead.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await loadZohoWidgetScript();

      const res = await fetch(`${ZOHO_PAYMENTS.workerUrl}/create-payment-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productKey, name, phone, note }),
      });
      const session = await res.json();
      if (!res.ok) {
        throw new Error(session.error || "Could not start the payment. Please try again.");
      }

      const instance = new window.ZPayments({
        account_id: ZOHO_PAYMENTS.accountId,
        domain: ZOHO_PAYMENTS.domain,
        otherOptions: {
          api_key: ZOHO_PAYMENTS.apiKey,
          ...(ZOHO_PAYMENTS.testMode ? { is_test_mode: true } : {}),
        },
      });

      await instance.requestPaymentMethod({
        amount: String(session.amount),
        currency_code: session.currency,
        payments_session_id: session.payments_session_id,
        description: session.description,
      });

      navigate(`/payment-success?ref=${encodeURIComponent(session.payments_session_id)}`);
    } catch (err) {
      if (err && err.code === "widget_closed") {
        setSubmitting(false);
        return;
      }
      navigate("/payment-failed");
    }
  }

  return (
    <form className="pay-button-form" onSubmit={handlePay}>
      <div className="mb-2">
        <input
          className="form-control"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={submitting}
        />
      </div>
      <div className="mb-2">
        <input
          className="form-control"
          placeholder="Phone number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={submitting}
        />
      </div>
      {error && <p className="pay-button-error">{error}</p>}
      <button type="submit" className="btn btn-primary pay-button" disabled={submitting}>
        {submitting ? "Starting payment…" : label}
      </button>
    </form>
  );
}
